import React, { useEffect, useRef, useState } from 'react';
import qs from 'qs';
import { useLocation } from 'react-router-dom';
import * as api from '../services/api-v4';
import { withLocalize } from 'react-localize-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import getLanguage from '../services/helpers';
import { Col, Container, Row } from 'reactstrap';
import TagManager from '@sooro-io/react-gtm-module';

const nrOfRetries = 0;
const retryDelayMs = 1000;

const language = getLanguage();

function KustomReceipt({ translate }) {
  const [showLoading, setShowLoading] = useState(true);
  const [showReceipt, setShowReceipt] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const { search } = useLocation();
  const qParams = qs.parse(search, { ignoreQueryPrefix: true });
  const { orderId, PayerID: payerId, token: paypalOrderId } = qParams;

  const history = useHistory();
  const containerRef = useRef(null);
  const purchaseEventSent = useRef(false);

  const sendPurchaseEvent = (response) => {
    if (purchaseEventSent.current) return;

    const orderId = response?.order_id;
    const storageKey = `gm_gtm_purchase_${orderId}`;

    // Check if event was already sent for this transaction
    if (localStorage.getItem(storageKey)) {
      console.log('Purchase event already sent for transaction:', orderId);
      return;
    }

    purchaseEventSent.current = true;
    localStorage.setItem(storageKey, 'true');

    console.log('Sending purchase event to GTM');

    // Send GTM event with available data
    const ecommerceData = {
      currency: response.purchase_currency,
      transaction_id: orderId
    };

    // Add value if available
    if (response.order_total_excl_vat) {
      ecommerceData.value = parseFloat(response.order_total_excl_vat);
      ecommerceData.tax = parseFloat(response.vat_amount || 0);
    }

    // Add items if available
    if (response.items) {
      ecommerceData.items = response.items.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        price: parseFloat(item.unit_price_excl_vat),
        quantity: parseInt(item.quantity, 10)
      }));
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'purchase',
        ecommerce: ecommerceData
      }
    });
  };

  useEffect(() => {
    if (!orderId && !payerId) {
      history.push(`/${language}/`);
      return;
    }

    localStorage.removeItem('gm_last_order');
    validatePaymentWithRetry(nrOfRetries);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const validatePaymentWithRetry = async (retriesLeft) => {
    const { response, error } = await api.validateKustomPayment({ orderId, payerId, paypalOrderId });

    if (error) {
      if (retriesLeft > 0) {
        setTimeout(() => {
          validatePaymentWithRetry(retriesLeft - 1);
        }, retryDelayMs);
      } else {
        setErrorMessage(error);
        setShowLoading(false);
      }
      return;
    }

    if (response?.html_snippet) {
      const { html_snippet: htmlSnippet } = response;
      const container = containerRef.current;
      container.innerHTML = htmlSnippet;

      const scriptTags = container.getElementsByTagName('script');

      for (let i = 0; i < scriptTags.length; i++) {
        const parent = scriptTags[i].parentNode;
        const newScript = document.createElement('script');
        newScript.type = 'text/javascript';
        if (scriptTags[i].src) {
          newScript.src = scriptTags[i].src;
        } else {
          newScript.text = scriptTags[i].text;
        }
        parent.removeChild(scriptTags[i]);
        parent.appendChild(newScript);
      }

      sendPurchaseEvent(response);
      setShowLoading(false);
    } else if (response?.status === 'COMPLETED') {
      sendPurchaseEvent(response);
      setShowReceipt(response);
      setShowLoading(false);
    }
  };

  return (
    <>
      <div className="payment-container">
        <div id="payment-view" className="px-5 py-5">
          <Row>
            <Col className="text-center" style={{ minHeight: 400 }}>
              {showLoading && (
                <>
                  <div className="spinner" style={{ marginBottom: '30px' }}>
                    <div className="dot1" />
                    <div className="dot2" />
                  </div>
                  <p>{translate('receipt.order-processing')}...</p>
                </>
              )}
              {errorMessage && (
                <p className="text-danger">
                  {translate('receipt.confirm-failed')} <br /> {errorMessage}
                </p>
              )}
              {showReceipt && (
                <Container>
                  <p className="text-left">
                    <strong>Vielen Dank für Ihren Einkauf!</strong>
                    <br />
                    <br /> Ihre Bestellung ist bei uns eingegangen und wird nun bearbeitet.
                    <br /> <strong>Bestellnummer:</strong> {showReceipt.purchase_units[0].reference_id}
                    <br />
                    <strong>Gesamtbetrag:</strong> {showReceipt.purchase_units[0].amount.value}{' '}
                    {showReceipt.purchase_units[0].amount.currency_code}
                    <br />
                    <br />
                    Eine Quittung wurde an die beim Kauf angegebene E-Mail-Adresse ({showReceipt.payer.email_address}) gesendet. Bei Fragen
                    zu
                    <br />
                    <br />
                    Ihrer Bestellung wenden Sie sich bitte an unseren Kundenservice.
                  </p>
                </Container>
              )}
              <div ref={containerRef} />
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
}

export default withLocalize(KustomReceipt);
